/**
 * Utilitaires de validation cohérence temporelle avancée
 * Corrige le feedback Viguen sur les faux positifs de dates
 */

interface CVExperience {
  id: string;
  startDate: string; // Format YYYY-MM
  endDate: string | null; // null si en cours
  company: string;
  position: string;
}

interface ReferentDates {
  collaboration_start_date: string;
  collaboration_end_date: string | null;
}

interface TemporalCoherenceResult {
  score: number; // 0-10
  isCoherent: boolean; // >= 7
  issues: string[];
  severity: 'low' | 'medium' | 'high';
  details: {
    overlapMonths: number;
    overlapPercentage: number;
    durationGapMonths: number;
    startDateGapMonths: number;
    endDateGapMonths: number;
    hasLogicalDates: boolean;
    cvDurationMonths: number;
    referentDurationMonths: number;
  };
}

/**
 * Convertit une date string en format YYYY-MM vers un objet Date
 */
function parseYearMonth(dateStr: string): Date {
  const [year, month] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, 1); // month - 1 car les mois JS commencent à 0
}

/**
 * Calcule la différence en mois entre deux dates
 */
function calculateMonthsDifference(date1: Date, date2: Date): number {
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();
  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();
  
  return (year2 - year1) * 12 + (month2 - month1);
}

/**
 * Calcule la durée en mois d'une période
 */
function calculateDurationMonths(startDate: string, endDate: string | null): number {
  const start = parseYearMonth(startDate);
  const end = endDate ? parseYearMonth(endDate) : new Date();
  
  return Math.max(1, calculateMonthsDifference(start, end) + 1); // +1 pour inclure le mois de début
}

/**
 * Calcule le chevauchement entre deux périodes
 */
function calculateOverlap(
  cvStart: string, 
  cvEnd: string | null, 
  refStart: string, 
  refEnd: string | null
): { overlapMonths: number; overlapPercentage: number } {
  try {
    const cvStartDate = parseYearMonth(cvStart);
    const cvEndDate = cvEnd ? parseYearMonth(cvEnd) : new Date();
    
    // Parser les dates du référent (gérer différents formats)
    let refStartDate: Date;
    let refEndDate: Date;
    
    if (refStart.includes('-')) {
      const refStartStr = refStart.substring(0, 7); // Prendre YYYY-MM
      refStartDate = parseYearMonth(refStartStr);
    } else {
      return { overlapMonths: 0, overlapPercentage: 0 };
    }
    
    if (refEnd) {
      if (refEnd.includes('-')) {
        const refEndStr = refEnd.substring(0, 7);
        refEndDate = parseYearMonth(refEndStr);
      } else {
        refEndDate = new Date();
      }
    } else {
      refEndDate = new Date();
    }
    
    // Calculer le chevauchement
    const overlapStart = new Date(Math.max(cvStartDate.getTime(), refStartDate.getTime()));
    const overlapEnd = new Date(Math.min(cvEndDate.getTime(), refEndDate.getTime()));
    
    if (overlapStart > overlapEnd) {
      return { overlapMonths: 0, overlapPercentage: 0 };
    }
    
    const overlapMonths = calculateMonthsDifference(overlapStart, overlapEnd) + 1;
    const cvDurationMonths = calculateDurationMonths(cvStart, cvEnd);
    const overlapPercentage = (overlapMonths / cvDurationMonths) * 100;
    
    return { overlapMonths, overlapPercentage };
  } catch (_error) {
    console.warn('Erreur calcul chevauchement:', _error);
    return { overlapMonths: 0, overlapPercentage: 0 };
  }
}

/**
 * Validation cohérence temporelle avancée (CORRIGÉE)
 * Résout le problème Viguen : "2019 vs 2019-2024 = OK"
 */
export function validateTemporalCoherence(
  cvExperience: CVExperience,
  referentDates: ReferentDates
): TemporalCoherenceResult {
  let score = 10;
  const issues: string[] = [];
  
  // Calculer les durées
  const cvDurationMonths = calculateDurationMonths(cvExperience.startDate, cvExperience.endDate);
  const referentDurationMonths = calculateDurationMonths(
    referentDates.collaboration_start_date ? referentDates.collaboration_start_date.substring(0, 7) : cvExperience.startDate,
    referentDates.collaboration_end_date ? referentDates.collaboration_end_date.substring(0, 7) : null
  );
  
  // Calculer les écarts de dates
  const startDateGapMonths = Math.abs(calculateMonthsDifference(
    parseYearMonth(cvExperience.startDate),
    parseYearMonth(referentDates.collaboration_start_date.substring(0, 7))
  ));
  
  let endDateGapMonths = 0;
  if (cvExperience.endDate && referentDates.collaboration_end_date) {
    endDateGapMonths = Math.abs(calculateMonthsDifference(
      parseYearMonth(cvExperience.endDate),
      parseYearMonth(referentDates.collaboration_end_date.substring(0, 7))
    ));
  }
  
  // Calculer le chevauchement
  const overlap = calculateOverlap(
    cvExperience.startDate,
    cvExperience.endDate,
    referentDates.collaboration_start_date,
    referentDates.collaboration_end_date
  );
  
  // Calculer l'écart de durée
  const durationGapMonths = Math.abs(cvDurationMonths - referentDurationMonths);
  
  // Vérifier la logique des dates
  const hasLogicalDates = (() => {
    try {
      if (referentDates.collaboration_end_date) {
        const start = parseYearMonth(referentDates.collaboration_start_date.substring(0, 7));
        const end = parseYearMonth(referentDates.collaboration_end_date.substring(0, 7));
        return start <= end;
      }
      return true;
    } catch {
      return false;
    }
  })();
  
  // SCORING CORRIGÉ
  
  // 1. Vérifier dates logiques
  if (!hasLogicalDates) {
    score -= 8;
    issues.push('Dates de collaboration incohérentes (fin avant début)');
  }
  
  // 2. CORRECTION MAJEURE : Vérifier pourcentage de chevauchement
  if (overlap.overlapMonths === 0) {
    score -= 7;
    issues.push('Aucun chevauchement temporel entre CV et déclaration référent');
  } else if (overlap.overlapPercentage < 30) {
    score -= 5;
    issues.push(`Chevauchement temporel insuffisant (${Math.round(overlap.overlapPercentage)}% seulement)`);
  } else if (overlap.overlapPercentage < 60) {
    score -= 3;
    issues.push(`Chevauchement temporel partiel (${Math.round(overlap.overlapPercentage)}%)`);
  }
  
  // 3. CORRECTION MAJEURE : Vérifier cohérence de durée (cas Viguen)
  const durationRatio = Math.min(referentDurationMonths, cvDurationMonths) / Math.max(referentDurationMonths, cvDurationMonths);
  
  if (durationGapMonths > 24) {
    score -= 4;
    issues.push(`Écart de durée majeur (${durationGapMonths} mois de différence)`);
  } else if (durationGapMonths > 12) {
    score -= 3;
    issues.push(`Écart de durée significatif (${durationGapMonths} mois de différence)`);
  } else if (durationRatio < 0.5) {
    score -= 2;
    issues.push(`Durées très différentes (CV: ${cvDurationMonths} mois, Référent: ${referentDurationMonths} mois)`);
  }
  
  // 4. Vérifier écarts de dates de début/fin
  if (startDateGapMonths > 6) {
    score -= 2;
    issues.push(`Écart important sur la date de début (${startDateGapMonths} mois)`);
  } else if (startDateGapMonths > 3) {
    score -= 1;
    issues.push(`Écart modéré sur la date de début (${startDateGapMonths} mois)`);
  }
  
  if (endDateGapMonths > 6) {
    score -= 2;
    issues.push(`Écart important sur la date de fin (${endDateGapMonths} mois)`);
  } else if (endDateGapMonths > 3) {
    score -= 1;
    issues.push(`Écart modéré sur la date de fin (${endDateGapMonths} mois)`);
  }
  
  // Assurer que le score reste entre 0 et 10
  score = Math.max(0, Math.min(10, score));
  
  // Déterminer la sévérité
  let severity: 'low' | 'medium' | 'high' = 'low';
  if (score <= 3) severity = 'high';
  else if (score <= 6) severity = 'medium';
  
  return {
    score,
    isCoherent: score >= 7,
    issues,
    severity,
    details: {
      overlapMonths: overlap.overlapMonths,
      overlapPercentage: overlap.overlapPercentage,
      durationGapMonths,
      startDateGapMonths,
      endDateGapMonths,
      hasLogicalDates,
      cvDurationMonths,
      referentDurationMonths
    }
  };
}

/**
 * Analyse la cohérence temporelle pour tous les référents d'une campagne
 */
export function analyzeCampaignTemporalCoherence(
  experiences: CVExperience[],
  referentResponses: Array<{
    referent_id: string;
    referent_name: string;
    experience_id: string;
    collaboration_start_date: string;
    collaboration_end_date: string | null;
  }>
): Array<{
  referent_id: string;
  referent_name: string;
  experience_id: string;
  coherence: TemporalCoherenceResult;
}> {
  return referentResponses.map(referent => {
    const linkedExperience = experiences.find(exp => exp.id === referent.experience_id);
    
    if (!linkedExperience) {
      return {
        referent_id: referent.referent_id,
        referent_name: referent.referent_name,
        experience_id: referent.experience_id,
        coherence: {
          score: 0,
          isCoherent: false,
          issues: ['Expérience liée non trouvée dans les données CV'],
          severity: 'high' as const,
          details: {
            overlapMonths: 0,
            overlapPercentage: 0,
            durationGapMonths: 0,
            startDateGapMonths: 0,
            endDateGapMonths: 0,
            hasLogicalDates: false,
            cvDurationMonths: 0,
            referentDurationMonths: 0
          }
        }
      };
    }
    
    const coherence = validateTemporalCoherence(linkedExperience, {
      collaboration_start_date: referent.collaboration_start_date,
      collaboration_end_date: referent.collaboration_end_date
    });
    
    return {
      referent_id: referent.referent_id,
      referent_name: referent.referent_name,
      experience_id: referent.experience_id,
      coherence
    };
  });
}

/**
 * Exemple de test pour le cas Viguen
 */
export function testViguenCase(): TemporalCoherenceResult {
  const cvExperience: CVExperience = {
    id: 'exp1',
    startDate: '2019-01',
    endDate: '2024-12',
    company: 'Entreprise Test',
    position: 'Senior Account Executive'
  };
  
  const referentDates: ReferentDates = {
    collaboration_start_date: '2019-01-01',
    collaboration_end_date: '2019-12-31' // Référent déclare seulement 2019 (12 mois vs 72 mois CV)
  };
  
  return validateTemporalCoherence(cvExperience, referentDates);
}